import { useCallback, useMemo, useState } from 'react';
import { map, find, capitalize } from 'lodash';
import {
  List, ListItemAvatar, Stack, ListItem,
  ListItemText,
} from '@mui/material';
import { useDispatch } from '@client/hooks/redux.hooks';
import { startSetSelectedProfile } from '@client/slices/helpers/session.reducer';
import { Avatar } from '@client/components/common/Avatar';
import { Button } from '@client/components/common/Button';
import { showSnackbar } from '@client/listeners/lib/snackbar';
import { Modal } from '@client/components/globals/Modal';
import { ProfileTypes } from '@sda/enums';
import ActPlaceholderImage from '@client/images/placeholders/artist-small.jpg';
import VenuePlaceholderImage from '@client/images/placeholders/venue-small.jpg';
import type { PublicAct } from '@sda/interfaces/act';
import type { PublicVenue } from '@sda/interfaces/venue';
import type { FetchDataType } from '@sda/sdk/interfaces';

interface SwitchProfileModalProps {
  readonly beaconProfileType: ProfileTypes;
  readonly profiles: ((PublicAct | PublicVenue) & FetchDataType)[];
}

export const SwitchProfileModal: React.FC<SwitchProfileModalProps> = ({ profiles, beaconProfileType }) => {
  const dispatch = useDispatch();

  const profileType = beaconProfileType === ProfileTypes.ACT ? ProfileTypes.VENUE : ProfileTypes.ACT;

  const [selectedProfileId, setSelectedProfileId] = useState<string>(null);
  const profile = useMemo(() => find(profiles, (item) => item.id === selectedProfileId), [profiles, selectedProfileId]);

  const handleSwitchProfileButtonClick = useCallback(() => {
    dispatch(startSetSelectedProfile({ profileId: selectedProfileId, profileType }));
  }, [selectedProfileId, profileType, dispatch]);

  return (
    <Stack
      spacing={3}
      sx={{ flex: 1 }}
    >
      <List>
        {map(profiles, (item) => (
          <ListItem
            onClick={() => setSelectedProfileId(item.id)}
            key={item.id}
            disableGutters
            divider
            button
          >
            <ListItemAvatar>
              <Avatar
                images={item.publicImageUrl}
                alt={item.name}
                fallback={profileType === ProfileTypes.ACT ? ActPlaceholderImage : VenuePlaceholderImage}
              />
            </ListItemAvatar>

            <ListItemText
              primary={item.name}
              secondary={capitalize(profileType)}
              primaryTypographyProps={{
                variant: item.id === selectedProfileId ? 'body2' : 'body1',
                color: item.id === selectedProfileId ? 'success' : 'textSecondary',
              }}
              secondaryTypographyProps={{
                variant: 'caption',
                color: 'textSecondary',
              }}
            />
          </ListItem>
        ))}
      </List>

      <div>
        <Button
          id="sda-swift-profile-button"
          scope={`select-profile-${selectedProfileId}`}
          variant="contained"
          color="primary"
          onClick={handleSwitchProfileButtonClick}
          onSuccess={() => {
            showSnackbar((
              <>
                {`Profile switched to ${profile.name}.`}

                <br />

                Switch profiles in Settings &gt; Profiles.
              </>
            ));
            Modal.hide();
          }}
          ButtonProps={{
            disabled: !selectedProfileId,
          }}
        >
          Switch Profile
        </Button>
      </div>
    </Stack>
  );
};
